// MUI
import { Skeleton, Stack, Typography } from "@mui/material";
// Third Party
import moment from "moment-timezone";

// Hooks
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";

// API
import { ingestSocialMediaReviews } from "@DataPages/api/socialMediaReviews";

// Utils
import { and } from "@Common/helpers/sonar";

// Components
import CountDownButton from "@Layouts/components/Buttons/CountDownButton";
import TextIconMessage from "@Layouts/components/Basics/TextIconMessage";

// Constants
import actionsList from "@Constants/actionsList";
import { DATE_FORMAT } from "@/Constants/date";

// Types
import { Nullable } from "@Common/interfaces/types";
import { ITransformedData } from "../interfaces";

type DataSyncInfoProps = {
	data: Nullable<ITransformedData>;
	isUpdatable: boolean;
	isLoading: boolean;
	showRefreshButton?: boolean;
	locationId: string;
	commissionCycleId: string;
	timezone: string | undefined;
	payPeriodEndDate: string;
	className?: string;
	/** optional: show countdown button */
	onRefresh?: () => void;
};

const CONFIRMATION_DIALOG_MESSAGE =
	"Updating this data will render review details unusable until the following day. You will not be able to see the individual reviews of the store and it’s associates. Do you wish to continue?";

export default function DataSyncInfo(props: DataSyncInfoProps) {
	const { data, isLoading, locationId, commissionCycleId: selectedCommissionCycle, timezone, payPeriodEndDate, className = "" } = props;

	const { useAuth } = useAuthorization();
	const SYNC_SOCIAL_MEDIA_REVIEWS = useAuth(actionsList.SYNC_SOCIAL_MEDIA_REVIEWS);

	/********************************************* HANDLERS ************************************************/
	const handleApiCall = async (locationId: string, commissionCycleId: string, payPeriodEndDate: string) => {
		ingestSocialMediaReviews(locationId, commissionCycleId, payPeriodEndDate);
	};

	return (
		<Stack direction={"row"} mt={2.25} spacing={2} alignItems={"center"} className={className}>
			<CountDownButton
				disabled={!SYNC_SOCIAL_MEDIA_REVIEWS}
				showButton={Boolean(props.isUpdatable)}
				typographySx={{ fontSize: "12px" }}
				initialCopy={"Update Now"}
				actionCopy={"Updating..."}
				loading={isLoading}
				confirmBeforeSubmit={and(Boolean(data?.usersReviews?.length), !Boolean(data?.onDemand))}
				confirmationDialogCopy={CONFIRMATION_DIALOG_MESSAGE}
				onClickCallBack={() => handleApiCall(locationId, selectedCommissionCycle, payPeriodEndDate)}
			/>
			<TextIconMessage
				icon
				title={"Last Update"}
				content={data?.updatedAt}
				showContent={Boolean(data?.updatedAt)}
				loading={isLoading}
				sx={{
					color: "var(--text-color)",
					marginTop: "2px",
				}}
			/>
			{isLoading ? (
				<Skeleton variant="text" width={210} height={20} />
			) : data?.onDemand ? (
				<Typography className={"regular-pt-sans-pro-narrow-label"} sx={{ marginLeft: "-10px" }}>
					{`(Review details are unusable until ${moment(data?.updatedAt)
						.tz(timezone ?? "America/New_York")
						.add(1, "day")
						.set({ hour: 9, minute: 0, second: 0, millisecond: 0 })
						.format(DATE_FORMAT.usDateTime12hPadded)})`}
				</Typography>
			) : null}
		</Stack>
	);
}
